import { type MouseEvent, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  DEFAULT_COMMUNITY_COLORS,
  WORK_ORDER_COLORS_MAP,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import {
  IAttemptListItemResponseForWorkOrder,
  WorkOrderStatus,
} from "@miplatform/types";
import {
  convertTimeToShortHand,
  getWorkOrderDueDateColor,
  getWorkOrderStatus,
} from "@miplatform/utils";
import { mdiCalendarAccountOutline, mdiCheckCircle, mdiMinus } from "@mdi/js";
import { ButtonProps, Checkbox, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiMenu } from "../MiMenu/MiMenu";
import { MiPopper } from "../MiPopper/MiPopper";
import { MiTemplateBadge } from "../MiTemplateBadge/MiTemplateBadge";
import { MiWorkOrderTooltip } from "./MiWorkOrderTooltip";

export const MiWorkOrderCard = ({
  attempt,
  minimized = false,
  className,
  loading = false,
  onEventRemove,
  onCardClick,
  isRemovable = false,
  menuItems = [],
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checked = false,
}: MiWorkOrderCardProps) => {
  const theme = useTheme();
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [isPopperOpen, handlePopperOpen, handlePopperClose] = useBoolean();
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    home,
    assignment,
    completedSteps = 0,
    totalSteps = 0,
  } = attempt ?? {};
  const { workOrder } = assignment ?? {};
  const { workOrderTemplateColor, abbreviation, workOrderTemplateName } =
    workOrder ?? {};
  const { address, builderShortName, communityName } = home ?? {};
  const { addressLine1, city } = address ?? {};
  const { hardDueDate } = workOrder ?? {};

  const status = getWorkOrderStatus(attempt);

  const {
    cardBackgroundColor = theme.palette.common.white,
    borderStyle,
    textColor,
    borderColor,
  } = (
    WORK_ORDER_COLORS_MAP as Record<
      WorkOrderStatus,
      {
        cardBackgroundColor?: string;
        textColor?: string;
        borderStyle?: string;
        borderColor?: string;
      }
    >
  )[status];

  const headerColor = useMemo(() => {
    const { communityName = "" } = home || {};
    const colors = DEFAULT_COMMUNITY_COLORS;
    if (communityName) {
      const colorIndex =
        communityName.split("").reduce((a, i) => a + i.charCodeAt(0), 0) %
        colors.length;
      return colors[colorIndex];
    }
    return theme.palette.bluegrey.light;
  }, [home?.communityName]);

  const dueDateColor = getWorkOrderDueDateColor(attempt, theme);

  const { classes, cx } = useStyles({
    borderColor:
      workOrder?.isLate && status !== WorkOrderStatus.Complete
        ? theme.palette.error.main
        : borderColor,
    borderStyle,
    headerColor: minimized ? "transparent" : headerColor,
    backgroundColor: cardBackgroundColor,
    minimized,
    loading,
  });

  const color = textColor ?? theme.palette.text.primary;

  const openPopper = (e: MouseEvent<HTMLDivElement>) => {
    const { currentTarget } = e;
    handlePopperOpen();
    setAnchorEl(currentTarget);
  };

  const closePopper = () => {
    setAnchorEl(null);
    handlePopperClose();
  };

  const handleContextMenuOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (isContextMenuOpen) {
      setIsContextMenuOpen(false);
      return;
    }

    handlePopperClose();
    setAnchorEl(e.currentTarget);
    setContextMenuPosition(
      !contextMenuPosition ? { left: e.clientX + 2, top: e.clientY - 6 } : null
    );
    setIsContextMenuOpen(true);
  };

  const handleContextMenuClose = () => {
    setIsContextMenuOpen(false);
    setContextMenuPosition(null);
    setAnchorEl(null);
  };

  const badgeText = abbreviation ?? workOrderTemplateName?.slice(0, 3) ?? null;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isBulkAssignmentMode) {
      e.stopPropagation();
      onCheckboxChange?.(attempt, !checked);
      return;
    }
    onCardClick?.(attempt);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onCheckboxChange?.(attempt, e.target.checked);
  };

  return (
    <Box
      className={cx(classes.card, className)}
      onContextMenu={handleContextMenuOpen}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
      onClick={handleClick}
    >
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {isBulkAssignmentMode && (
            <Checkbox
              sx={{
                "& .MuiSvgIcon-root": { fontSize: 14 },
                padding: 0,
              }}
              size="small"
              color="primary"
              checked={checked}
              onChange={handleCheckboxChange}
              onClick={(ev) => ev.stopPropagation()}
            />
          )}
          {badgeText && (
            <MiTemplateBadge
              title={badgeText}
              backgroundColor={workOrderTemplateColor}
            />
          )}
          <Typography fontSize={9} noWrap>
            {communityName}
          </Typography>
        </Box>
        <Box className={classes.headerRight}>
          <Typography
            fontSize={9}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.grey.medium}
            fontStyle="italic"
            pr={0.25}
            noWrap
          >
            {builderShortName}
          </Typography>
          {isRemovable && (
            <MiButton
              aria-label="Remove"
              size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
              variant={BUTTON_VARIANTS.CONTAINED}
              color={BUTTON_COLORS.ERROR}
              className={classes.removeButton}
              startIcon={{
                path: mdiMinus,
                iconSize: 0.45,
                iconColor: theme.palette.common.white,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEventRemove?.();
              }}
            />
          )}
        </Box>
      </Box>
      {!minimized && (
        <Box className={classes.body}>
          <Box className={classes.row}>
            <Typography
              fontSize={9}
              color={color}
              textOverflow="ellipsis"
              noWrap
            >
              {addressLine1}
            </Typography>
            <Typography
              fontSize={9}
              color={color ?? theme.palette.grey.medium}
              fontStyle="italic"
              pr={0.25}
            >
              {city}
            </Typography>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.row} alignItems="center">
              {totalSteps > 0 &&
                (completedSteps === totalSteps ? (
                  <MiIcon
                    path={mdiCheckCircle}
                    size={0.5}
                    color={theme.palette.success.main}
                  />
                ) : (
                  <Typography
                    fontSize={9}
                    color={color}
                  >{`(${completedSteps}/${totalSteps})`}</Typography>
                ))}
            </Box>
            <Box className={classes.row}>
              {hardDueDate && (
                <Typography fontSize={9} color={dueDateColor}>
                  Due: {convertTimeToShortHand(hardDueDate)}
                </Typography>
              )}
              {status === WorkOrderStatus.UnableToBeAutoAssigned && (
                <MiIcon
                  path={mdiCalendarAccountOutline}
                  color={theme.palette.warning.main}
                  size={0.5}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
      {menuItems.length > 0 && (
        <MiMenu
          condensed
          data={menuItems}
          menuStyles={{ zIndex: theme.zIndex.tooltip }}
          optionalElement={<span />}
          closeContextMenu={handleContextMenuClose}
          open={isContextMenuOpen}
          anchorPosition={contextMenuPosition}
        />
      )}
      <MiPopper
        open={isPopperOpen}
        anchorEl={anchorEl}
        arrowColor={workOrderTemplateColor}
        showArrow
      >
        <MiWorkOrderTooltip attempt={attempt} />
      </MiPopper>
    </Box>
  );
};

export interface MiWorkOrderCardProps {
  attempt: IAttemptListItemResponseForWorkOrder;
  minimized?: boolean;
  className?: string;
  loading?: boolean;
  menuItems?: {
    label: string;
    icon: string;
    color: string;
    action: () => void;
    hide?: boolean;
  }[];
  onCardClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  onEventRemove?: () => void;
  isRemovable?: boolean;
  isBulkAssignmentMode?: boolean;
  onCheckboxChange?: (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => void;
  checked?: boolean;
}

const useStyles = makeStyles<IWorkOrderCardStyleProps>()(
  (
    theme,
    {
      borderColor,
      headerColor,
      backgroundColor,
      borderStyle = "solid",
      loading,
    }
  ) => {
    return {
      card: {
        border: `1px ${borderStyle} ${borderColor}`,
        borderRadius: 4,
        backgroundColor,
        overflow: "hidden",
        height: "100%",
        width: 160,
        "&:hover": {
          cursor: "grab",
        },
        "&:active": { cursor: "grabbing", opacity: 0.5 },
        opacity: loading ? 0.5 : 1,
        pointerEvents: loading ? "none" : "auto",
      },
      header: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5),
        backgroundColor: headerColor,
        height: theme.spacing(4),
      },
      body: {
        padding: theme.spacing(0.5),
      },
      row: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
      },
      headerLeft: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
      },
      headerRight: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: theme.spacing(0.25),
        flexShrink: 0,
        minWidth: 0,
      },
      removeButton: {
        "&&": {
          flexShrink: 0,
          width: 14,
          height: 14,
          minWidth: 14,
          minHeight: 14,
          padding: 0,
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: theme.palette.error.dark,
          },
          "& .MuiButton-startIcon": {
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        },
      },
    };
  }
);

export interface IWorkOrderCardStyleProps {
  borderColor?: string;
  headerColor?: string;
  backgroundColor?: string;
  borderStyle?: string;
  minimized?: boolean;
  loading?: boolean;
}
